import { useQueryClient } from '@tanstack/react-query';
import { FC } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import Button from '@/components/Button';
import { FormLayout, Label, Larger, Textarea } from '@/components/Forms';
import Note from '@/components/Forms/Note';
import ValidationMessage from '@/components/Forms/ValidationMessage';
import { Modal } from '@/components/Modal';
import SidePanel, {
  SidePanelBody,
  SidePanelContent,
  SidePanelFooter,
} from '@/components/SidePanel';
import useAlerts from '@/hooks/useAlerts';
import { api } from '@/utils/api';
import { handleFormErrors } from '@/utils/helpers';

interface FormValues {
  message: string;
}

interface Props {
  onClose: () => void;
}

const CreateCommentModal: FC<Props> = ({ onClose }) => {
  const { id } = useParams();
  const queryClient = useQueryClient();
  const alerts = useAlerts();

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>();

  const onSubmit = handleSubmit(async ({ message }) => {
    try {
      await api.post(`/v1/editorial/facebook-posts/${id}/post-comment`, {
        message,
      });

      await queryClient.invalidateQueries([
        'editorial_distribution_schedules',
        {
          id,
        },
      ]);

      alerts.success('Comment created');
      onClose();
    } catch (err) {
      handleFormErrors(err, setError);
    }
  });

  return (
    <Modal>
      <SidePanel heading="Create comment" onClose={onClose}>
        <SidePanelBody autoComplete="off" onSubmit={onSubmit}>
          <SidePanelContent>
            <FormLayout>
              <Larger>
                <Note>
                  This will publish a new comment on Facebook.
                </Note>
              </Larger>
              <Larger>
                <Label>Comment</Label>
                <Textarea
                  {...register('message', {
                    required: 'Comment is required',
                    maxLength: {
                      value: 8000,
                      message: 'Comment is too long (max 8000 characters)',
                    },
                  })}
                  placeholder="Write a comment..."
                />
                {!!errors.message && (
                  <ValidationMessage>
                    {errors.message.message as string}
                  </ValidationMessage>
                )}
              </Larger>
            </FormLayout>
          </SidePanelContent>
          <SidePanelFooter $actions>
            <Button disabled={isSubmitting} loading={isSubmitting} type="submit">
              Create
            </Button>
            <Button color="secondary" onClick={onClose} type="button">
              Cancel
            </Button>
          </SidePanelFooter>
        </SidePanelBody>
      </SidePanel>
    </Modal>
  );
};

export default CreateCommentModal;
